import styled from '@emotion/styled'
import React, { useState } from 'react'
import { BrandingSettings } from './BusinessCalendarQueries'

type Props = {
  contentFeed: {
    id: string
    databaseId: number
    title: string
  } | null
  brandingSettings: BrandingSettings
}

const EmbedCodeGenerator: React.FC<Props> = ({ contentFeed, brandingSettings }) => {
  const [copied, setCopied] = useState(false)

  if (!contentFeed) {
    return (
      <Section>
        <SectionTitle>Embed Code</SectionTitle>
        <EmptyText>Your calendar feed is being set up. Check back shortly.</EmptyText>
      </Section>
    )
  }

  const feedUrl = `${window.location.origin}/feeds/${contentFeed.databaseId}/events`
  const primaryColor = brandingSettings.primaryColor || '#6CC14C'

  const embedCode = `<script
  src="${window.location.origin}/embeds/civiclift.js"
  data-civiclift-type="calendar"
  data-civiclift-url="${feedUrl}"
  data-civiclift-brand-color="${primaryColor}"
  data-civiclift-title="${contentFeed.title} events"
></script>`

  const handleCopy = () => {
    navigator.clipboard.writeText(embedCode).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <Section>
      <SectionTitle>Embed Code</SectionTitle>
      <SectionDescription>
        Add this code to the page on your website where you want your calendar to appear.
      </SectionDescription>

      <CodeBlock>
        <Code>{embedCode}</Code>
        <CopyButton onClick={handleCopy}>{copied ? 'Copied!' : 'Copy Code'}</CopyButton>
      </CodeBlock>

      <SetupTips>
        <SetupTipsTitle>How to add this to your website</SetupTipsTitle>
        <SetupTipsList>
          <li>Paste the code on the page where you want your calendar to live.</li>
          <li>It is recommended that you place it directly under your site&apos;s header and navigation.</li>
          <li>The calendar will automatically resize to fit the width of its container.</li>
        </SetupTipsList>
      </SetupTips>
    </Section>
  )
}

export default EmbedCodeGenerator

const Section = styled.div``

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #102638;
  margin: 0 0 8px;
`

const SectionDescription = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0 0 24px;
`

const EmptyText = styled.p`
  font-size: 14px;
  color: #9ca3af;
  margin: 16px 0;
`

const CodeBlock = styled.div`
  position: relative;
  background: #1e293b;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 32px;
`

const Code = styled.pre`
  font-family: 'SF Mono', Monaco, Consolas, monospace;
  font-size: 13px;
  color: #e2e8f0;
  white-space: pre-wrap;
  word-break: break-all;
  margin: 0;
  line-height: 1.6;
`

const CopyButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(255, 255, 255, 0.1);
  color: #e2e8f0;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 14px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`

const SetupTips = styled.div`
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px 20px;
`

const SetupTipsTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
`

const SetupTipsList = styled.ol`
  margin: 0;
  padding-left: 20px;
  font-size: 13px;
  color: #6b7280;
  line-height: 1.6;

  li {
    margin-bottom: 4px;
  }

  code {
    background: #e5e7eb;
    padding: 1px 4px;
    border-radius: 3px;
    font-size: 12px;
    color: #374151;
  }
`

